import { formatDistanceToNow } from 'date-fns';
import type { Plan } from '@/hooks/useUserPlans';
import { PlanTypeEnum } from '@/hooks/useUserPlans';

// TODO - Deprecate re-exporting types. Requires referring imports to be updated.
export { PlanTypeEnum as PlanType };

export const defaultPlan: Plan = {
  id: '',
  name: '',
  amount: 0,
  interval: 'year',
  currencyISO: '',
  canTrial: false,
  type: PlanTypeEnum.Free,
  visible: false,
  features: {
    institutionalQualityData: false,
    globalMarkets: false,
    companyReportsPerMonth: 0,
    companiesAnalysedInPortfolio: 0,
    watchlist: 0,
    totalPortfolios: 0,
    stockScreener: false,
    exportToPdf: false,
    dataAudit: false,
    stockIdeaViews: false,
  },
};

export interface Feature {
  code: string;
  text: string;
}

/**
 * Re-order this list to change feature order on all plan views
 */
export const featureList: Feature[] = [
  { code: 'globalMarkets', text: 'Global Markets Access' },
  { code: 'companyReportsPerMonth', text: 'Company Reports per Month' },
  { code: 'companiesAnalysedInPortfolio', text: 'Portfolio Stocks Analysed' },
  { code: 'watchlist', text: 'Unlimited Watchlists' },
  { code: 'stockScreener', text: 'Stock Screener' },
  { code: 'exportToPdf', text: 'Excel & PDF Export' },
];

export const PlanMarketingText = {
  [PlanTypeEnum.Free]: 'Free forever',
  [PlanTypeEnum.Premium]: 'Enough to keep on top of my investments',
  [PlanTypeEnum.Unlimited]: 'All the stocks, all the data, peace of mind',
};

export enum PlanCTA {
  SignUp = 'Sign up',
  Subscribe = 'Subscribe',
  Current = 'Current',
  Downgrade = 'Downgrade',
  Upgrade = 'Upgrade',
  Trial = 'Trial', //Show customer trial countdown
  Continue = 'Continue', //Ask customer to continue with free plan when trial ends
  Disabled = 'Disabled', //When the other plan was cancelled this one remains disabled
}
export const planButtonCTA = {
  //current plan
  learner: {
    // plan button
    learner: PlanCTA.Current,
    investor: PlanCTA.Upgrade,
    professional: PlanCTA.Upgrade,
    advisor: PlanCTA.Upgrade,
  },
  investor: {
    learner: PlanCTA.Downgrade,
    investor: PlanCTA.Current,
    professional: PlanCTA.Upgrade,
    advisor: PlanCTA.Upgrade,
  },
  professional: {
    learner: PlanCTA.Downgrade,
    investor: PlanCTA.Downgrade,
    professional: PlanCTA.Current,
    advisor: PlanCTA.Upgrade,
  },
  advisor: {
    learner: PlanCTA.Downgrade,
    investor: PlanCTA.Downgrade,
    professional: PlanCTA.Downgrade,
    advisor: PlanCTA.Current,
  },
};

export enum UserStatus {
  guest = 'guest',
  free = 'free',
  trial = 'trial',
  trialEnded = 'trialEnded',
  canceled = 'canceled',
  premiumMonthly = 'premiumMonth',
  premiumYearly = 'premiumYear',
  unlimitedMonthly = 'unlimitedMonth',
  unlimitedYearly = 'unlimitedYear',
}

export const defaultPlansPrice = {
  [UserStatus.premiumMonthly]: 0,
  [UserStatus.premiumYearly]: 0,
  [UserStatus.unlimitedMonthly]: 0,
  [UserStatus.unlimitedYearly]: 0,
};
export const userStatusData = {
  [PlanTypeEnum.Premium]: {
    year: {
      status: UserStatus.premiumYearly,
    },
    month: {
      status: UserStatus.premiumMonthly,
    },
  },
  [PlanTypeEnum.Unlimited]: {
    year: {
      status: UserStatus.unlimitedYearly,
    },
    month: {
      status: UserStatus.unlimitedMonthly,
    },
  },
};

export type BillingPeriod = 'year' | 'month';

export const DefaultButtonState = {
  cta: 'Get Started',
  btnStyle: 'gentleLargeReverse',
  action: 'login',
};
const CurrentPlan = {
  cta: 'Current Plan',
  btnStyle: 'gentleLargeReverse',
  action: 'disabled',
};
const downgrade = (hasCanceled: boolean) => ({
  cta: 'Downgrade',
  btnStyle: 'loudLarge',
  action: hasCanceled ? 'renew' : 'downgrade',
});
const choosePremium = (hasCanceled: boolean) => ({
  cta: 'Choose Premium',
  btnStyle: 'louderLarge',
  action: hasCanceled ? 'renew' : 'upgrade',
});
const Renew = {
  cta: 'Renew',
  btnStyle: 'louderLarge',
  action: 'upgrade',
};
const chooseUnlimited = (hasCanceled: boolean) => ({
  cta: 'Choose Unlimited',
  btnStyle: 'louderLarge',
  action: hasCanceled ? 'renew' : 'upgrade',
});
const switchToAnnual = (hasCanceled: boolean) => ({
  cta: 'Switch to Annual and Save',
  btnStyle: 'louderLarge',
  action: hasCanceled ? 'renew' : 'upgrade',
});
const switchToMonthly = (hasCanceled: boolean) => ({
  cta: 'Switch to Monthly Plan',
  btnStyle: 'gentleLargeReverse',
  action: hasCanceled ? 'renew' : 'upgrade',
});
const canceledPlan = (currentPlan: string, daysUntilCancel: string) => ({
  cta: `${currentPlan} plan expires in ${daysUntilCancel}`,
  btnStyle: 'gentleLargeReverse',
  action: 'disabled',
});

export const planButtonState = (
  hasCanceled: boolean,
  canceledAt: number,
  currentPlan: string
) => {
  const daysUntilCancel =
    canceledAt !== 0 && canceledAt !== null
      ? formatDistanceToNow(canceledAt)
      : '';
  //button
  return {
    [PlanTypeEnum.Free]: {
      //user's current status / plan
      [UserStatus.guest]: DefaultButtonState,
      [UserStatus.free]: CurrentPlan,
      [UserStatus.trial]: {
        cta: `Currently on ${currentPlan} Trial`,
        btnStyle: 'gentleLargeReverse',
        action: 'disabled',
      },
      [UserStatus.trialEnded]: {
        cta: 'Continue with limited features',
        btnStyle: 'gentleLargeReverse',
        action: 'continue',
      },
      [UserStatus.premiumMonthly]: hasCanceled
        ? canceledPlan(currentPlan, daysUntilCancel)
        : downgrade(hasCanceled),
      [UserStatus.premiumYearly]: hasCanceled
        ? canceledPlan(currentPlan, daysUntilCancel)
        : downgrade(hasCanceled),
      [UserStatus.unlimitedMonthly]: hasCanceled
        ? canceledPlan(currentPlan, daysUntilCancel)
        : downgrade(hasCanceled),
      [UserStatus.unlimitedYearly]: hasCanceled
        ? canceledPlan(currentPlan, daysUntilCancel)
        : downgrade(hasCanceled),
    },
    [PlanTypeEnum.Premium]: {
      [UserStatus.guest]: {
        cta: 'Start Free Trial',
        btnStyle: 'louderLarge',
        action: 'login',
      },
      [UserStatus.free]: choosePremium(hasCanceled),
      [UserStatus.trial]: {
        cta: 'Upgrade Now',
        btnStyle: 'louderLarge',
        action: 'upgrade',
      },
      [UserStatus.trialEnded]: choosePremium(hasCanceled),
      [UserStatus.premiumMonthly]: {
        year: switchToAnnual(hasCanceled),
        month: hasCanceled ? Renew : CurrentPlan,
      },
      [UserStatus.premiumYearly]: {
        month: switchToMonthly(hasCanceled),
        year: hasCanceled ? Renew : CurrentPlan,
      },
      [UserStatus.unlimitedMonthly]: {
        year: downgrade(hasCanceled),
        month: downgrade(hasCanceled),
      },
      [UserStatus.unlimitedYearly]: {
        month: downgrade(hasCanceled),
        year: downgrade(hasCanceled),
      },
    },
    [PlanTypeEnum.Unlimited]: {
      [UserStatus.guest]: {
        cta: 'Choose Unlimited',
        btnStyle: 'louderLarge',
        action: 'login',
      },
      [UserStatus.free]: chooseUnlimited(hasCanceled),
      [UserStatus.trial]: chooseUnlimited(hasCanceled),
      [UserStatus.trialEnded]: chooseUnlimited(hasCanceled),
      [UserStatus.premiumMonthly]: {
        year: chooseUnlimited(hasCanceled),
        month: chooseUnlimited(hasCanceled),
      },
      [UserStatus.premiumYearly]: {
        month: chooseUnlimited(hasCanceled),
        year: chooseUnlimited(hasCanceled),
      },
      [UserStatus.unlimitedMonthly]: {
        year: switchToAnnual(hasCanceled),
        month: hasCanceled ? Renew : CurrentPlan,
      },
      [UserStatus.unlimitedYearly]: {
        month: switchToMonthly(hasCanceled),
        year: hasCanceled ? Renew : CurrentPlan,
      },
    },
  };
};

export const samplePdfUrl =
  'https://media.simplywall.st/site/pro-plan/nasdaqgs-amzn.pdf';
export const samplePdfBrandedUrl =
  'https://media.simplywall.st/site/pro-plan/nasdaqgs-amnz-branded.pdf';
export const sampleCsvUrl =
  'https://media.simplywall.st/site/pro-plan/nasdaqgs-amzn.csv';

export const GrandfatherCoupons = [
  'ynWCzwhz',
  'fkXKDeJw',
  'rXSzIcDS',
  'faIlKV8K',
  'CGghaNKj',
  'JriRYwxp',
];
